// Cloudinary service for admin dashboard
// Uploads go straight to Cloudinary through an UNSIGNED preset — no secret
// needed, and none is held here. Anything that requires the account secret
// (delete, usage) goes through the backend: see pear-payments-backend/src/
// cloudinaryAdmin.js. The secret used to sit in this file, which meant it
// shipped in the JS bundle and was readable with view-source.

import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { isPaymentsApiConfigured, paymentsApiUrl } from './paymentsBaseUrl';

export interface CloudinaryConfig {
  cloudName: string;
  apiKey: string;
  uploadPreset: string;
}

export interface UploadResult {
  success: boolean;
  url?: string;
  publicId?: string;
  error?: string;
}

class CloudinaryService {
  private static instance: CloudinaryService;
  private config: CloudinaryConfig;

  // Public identifiers only. The cloud name and API key are not secrets — they
  // travel in every upload request anyway. The API SECRET is deliberately absent:
  // it belongs on the server, and holding it here put full account control
  // (list, download, delete every asset) into the browser bundle.
  private readonly CLOUDINARY_CONFIG: CloudinaryConfig = {
    // Switched off `du50l5uod` on 2026-08-21 — that environment sits in a
    // Cloudinary account nobody could claim. `debvrvfpu` is the one Gabriel
    // owns. Pre-existing images still resolve from the old cloud via their
    // stored URLs; they need a separate backup.
    cloudName: 'debvrvfpu',
    apiKey: '195249239761646',
    uploadPreset: 'pear_admin_unsigned'
  };

  private constructor() {
    this.config = this.CLOUDINARY_CONFIG;
    this.initialize();
  }

  public static getInstance(): CloudinaryService {
    if (!CloudinaryService.instance) {
      CloudinaryService.instance = new CloudinaryService();
    }
    return CloudinaryService.instance;
  }

  private initialize(): void {
    // Deliberately logs NOTHING about the credentials. This previously printed the
    // cloud name, API key and base URL into the browser console of every visitor,
    // which advertised the account and pointed straight at the config object — and
    // the API secret lives in that same object, in this same bundle. Diagnostics
    // are not worth handing an attacker the map.
  }

  private getBaseUrl(): string {
    return `https://api.cloudinary.com/v1_1/${this.config.cloudName}`;
  }

  private getUploadUrl(): string {
    return `${this.getBaseUrl()}/image/upload`;
  }

  /**
   * Test basic internet connectivity
   */
  public async testInternetConnection(): Promise<boolean> {
    try {
      console.log('🌐 [CloudinaryService] Testing basic internet connection...');
      await fetch('https://www.google.com', { 
        method: 'HEAD',
        mode: 'no-cors'
      });
      console.log('🌐 [CloudinaryService] Google response: 200');
      return true;
    } catch (error) {
      console.error('🌐 [CloudinaryService] Internet test failed:', error);
      return false;
    }
  }

  /**
   * Test Cloudinary API connectivity
   */
  public async testCloudinaryConnection(): Promise<boolean> {
    try {
      console.log('🧪 [CloudinaryService] Testing connection...');
      
      // Test with a simple API call
      const testUrl = `${this.getBaseUrl()}/resources/image?api_key=${this.config.apiKey}&timestamp=${Date.now()}&signature=test`;
      
      const response = await fetch(testUrl);
      console.log('🧪 [CloudinaryService] Test response status:', response.status);
      
      if (response.status === 401) {
        console.log('❌ [CloudinaryService] Connection test failed: 401');
        console.log('⚠️ [CloudinaryService] Will try unsigned uploads anyway...');
        return true; // Still return true as unsigned uploads might work
      }
      
      console.log('✅ [CloudinaryService] Connection test passed');
      return true;
    } catch (error) {
      console.error('❌ [CloudinaryService] Connection test failed:', error);
      return false;
    }
  }

  /**
   * Upload image to Cloudinary using unsigned upload
   */
  public async uploadImage(file: File, folder?: string): Promise<UploadResult> {
    try {
      console.log('📤 [CloudinaryService] Starting image upload...');
      
      // Test connectivity first
      const internetOk = await this.testInternetConnection();
      if (!internetOk) {
        return {
          success: false,
          error: 'No internet connection'
        };
      }

      // Create FormData for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', this.config.uploadPreset);
      
      if (folder) {
        formData.append('folder', folder);
      }

      // Add some metadata
      formData.append('tags', 'trasseapp');
      formData.append('context', `alt=${file.name}`);

      console.log('📤 [CloudinaryService] Uploading to:', this.getUploadUrl());
      
      const response = await fetch(this.getUploadUrl(), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [CloudinaryService] Upload failed:', response.status, errorText);
        return {
          success: false,
          error: `Upload failed: ${response.status} - ${errorText}`
        };
      }

      const result = await response.json();
      console.log('✅ [CloudinaryService] Upload successful:', result.public_id);
      
      return {
        success: true,
        url: result.secure_url,
        publicId: result.public_id
      };

    } catch (error) {
      console.error('❌ [CloudinaryService] Upload error:', error);
      return {
        success: false,
        error: `Upload error: ${error}`
      };
    }
  }

  /** Authorization header for the backend, or null when nobody is signed in. */
  private async authHeader(): Promise<Record<string, string> | null> {
    const token = await auth.currentUser?.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : null;
  }

  /** The school on this login. A cleanup of one school can pass that school
   *  instead, which is how a platform admin deletes another school's photos. */
  private async signedInOrgCode(): Promise<string> {
    const uid = auth.currentUser?.uid;
    if (!uid) return '';
    const snap = await getDoc(doc(db, 'users', uid));
    const data = snap.data();
    return String(data?.orgCode || data?.schoolCode || data?.organizationCode || '').trim();
  }

  /**
   * Delete image from Cloudinary — via the backend, which holds the secret.
   *
   * This used to sign the request in the browser with a hand-rolled 32-bit
   * hash that Cloudinary could never have accepted (the code said so itself:
   * "this is not secure for production"), so deletion had almost certainly
   * never worked. It works now, and the secret is no longer in the bundle.
   * The school goes with the request so the backend can refuse a person from
   * another school.
   */
  public async deleteImage(publicId: string, orgCode?: string): Promise<boolean> {
    try {
      if (!isPaymentsApiConfigured()) {
        console.error('❌ [CloudinaryService] Delete unavailable: backend not configured.');
        return false;
      }
      const headers = await this.authHeader();
      if (!headers) {
        console.error('❌ [CloudinaryService] Delete unavailable: not signed in.');
        return false;
      }
      const school = String(orgCode || await this.signedInOrgCode() || '').trim();
      if (!school) {
        console.error('❌ [CloudinaryService] Delete unavailable: no school on this account.');
        return false;
      }

      const response = await fetch(paymentsApiUrl('/api/media/cloudinary/destroy'), {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ publicId, orgCode: school }),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.ok) {
        console.error('❌ [CloudinaryService] Delete failed:', result.message || response.status);
        return false;
      }
      return true;
    } catch (error) {
      console.error('❌ [CloudinaryService] Delete error:', error);
      return false;
    }
  }

  /**
   * Get optimized image URL with transformations
   */
  public getOptimizedImageUrl(publicId: string, transformations?: {
    width?: number;
    height?: number;
    quality?: string;
    format?: string;
  }): string {
    let url = `https://res.cloudinary.com/${this.config.cloudName}/image/upload`;
    
    if (transformations) {
      const params: string[] = [];
      
      if (transformations.width) params.push(`w_${transformations.width}`);
      if (transformations.height) params.push(`h_${transformations.height}`);
      if (transformations.quality) params.push(`q_${transformations.quality}`);
      if (transformations.format) params.push(`f_${transformations.format}`);
      
      if (params.length > 0) {
        url += `/${params.join(',')}`;
      }
    }
    
    url += `/${publicId}`;
    return url;
  }

  /**
   * Get Cloudinary account usage statistics
   * Returns storage usage in bytes, or null if API call fails
   */
  public async getAccountUsage(): Promise<number | null> {
    try {
      if (!isPaymentsApiConfigured()) return null;
      const headers = await this.authHeader();
      if (!headers) return null;

      const response = await fetch(paymentsApiUrl('/api/media/cloudinary/usage'), { headers });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.ok) {
        console.warn('⚠️ [CloudinaryService] Usage read failed:', result.message || response.status);
        return null;
      }
      return typeof result.storageBytes === 'number' ? result.storageBytes : null;
    } catch (error) {
      console.error('❌ [CloudinaryService] Error getting account usage:', error);
      return null;
    }
  }

  /**
   * Run comprehensive Cloudinary tests
   */
  public async runTests(): Promise<void> {
    console.log('🧪 ===== CLOUDINARY TESTS STARTING =====');
    
    // Test 1: Internet connectivity
    console.log('\n📡 Test 1: Basic Internet Connectivity');
    const internetOk = await this.testInternetConnection();
    if (!internetOk) {
      console.log('❌ FAILED: No internet connection');
      return;
    }
    console.log('✅ PASSED: Internet connection working');
    
    // Test 2: Cloudinary API connectivity
    console.log('\n☁️ Test 2: Cloudinary API Connectivity');
    const cloudinaryOk = await this.testCloudinaryConnection();
    if (!cloudinaryOk) {
      console.log('❌ FAILED: Cloudinary API connection');
      return;
    }
    console.log('✅ PASSED: Cloudinary API connection working');
    
    // Test 3: URL generation
    console.log('\n🔗 Test 3: URL Generation');
    const testUrl = this.getOptimizedImageUrl('test_image', { width: 300, quality: 'auto' });
    console.log('✅ PASSED: URL generated:', testUrl);
    
    console.log('\n🎉 ===== ALL CLOUDINARY TESTS PASSED =====');
    console.log('✅ Cloudinary is properly configured and ready to use!');
  }
}

// Export singleton instance
export const cloudinaryService = CloudinaryService.getInstance();

// Export the class for direct instantiation if needed
export default CloudinaryService;
